import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
  passwordExpired: boolean;
  isApproved: boolean;
  approvalLoading: boolean;
  dedicatedSubscription: {
    subscribed: boolean;
    subscriptionEnd: string | null;
    loading: boolean;
  };
  refreshSubscription: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
  passwordExpired: false,
  isApproved: false,
  approvalLoading: true,
  dedicatedSubscription: { subscribed: false, subscriptionEnd: null, loading: false },
  refreshSubscription: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordExpired, setPasswordExpired] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [approvalLoading, setApprovalLoading] = useState(true);
  const [dedicatedSubscription, setDedicatedSubscription] = useState({
    subscribed: false,
    subscriptionEnd: null as string | null,
    loading: false,
  });

  const checkSubscription = useCallback(async (force = false) => {
    // Throttle: reuse recent result to avoid 429s from repeated auth events.
    const CACHE_KEY = "dedicated_sub_cache_v1";
    const TTL_MS = 5 * 60 * 1000; // 5 minutes
    if (!force) {
      try {
        const raw = sessionStorage.getItem(CACHE_KEY);
        if (raw) {
          const cached = JSON.parse(raw) as { at: number; subscribed: boolean; subscription_end: string | null };
          if (Date.now() - cached.at < TTL_MS) {
            setDedicatedSubscription({
              subscribed: cached.subscribed,
              subscriptionEnd: cached.subscription_end,
              loading: false,
            });
            return;
          }
        }
      } catch { /* ignore */ }
    }
    setDedicatedSubscription((prev) => ({ ...prev, loading: true }));
    try {
      const { data, error } = await supabase.functions.invoke("check-subscription");
      if (error) throw error;
      const subscribed = data?.subscribed ?? false;
      const subscription_end = data?.subscription_end ?? null;
      setDedicatedSubscription({ subscribed, subscriptionEnd: subscription_end, loading: false });
      try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), subscribed, subscription_end })); } catch { /* ignore */ }
    } catch {
      setDedicatedSubscription((prev) => ({ ...prev, loading: false }));
    }
  }, []);


  const checkApproval = useCallback(async (userId: string) => {
    setApprovalLoading(true);
    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_approved")
        .eq("user_id", userId)
        .single();
      setIsApproved(data?.is_approved ?? false);
    } catch {
      setIsApproved(false);
    }
    setApprovalLoading(false);
  }, []);

  const checkPasswordExpiry = useCallback(async (userId: string) => {
    try {
      const { data } = await supabase
        .from("profiles")
        .select("password_changed_at")
        .eq("user_id", userId)
        .single();
      if (data?.password_changed_at) {
        const changedAt = new Date(data.password_changed_at);
        const sixMonthsAgo = new Date();
        sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
        setPasswordExpired(changedAt < sixMonthsAgo);
      }
    } catch {
      // If we can't check, don't block the user
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        // Only invoke check-subscription on actual sign-in, not on token refresh.
        if (event === "SIGNED_IN" || event === "INITIAL_SESSION") {
          setTimeout(() => checkSubscription(), 0);
        }
        setTimeout(() => checkPasswordExpiry(session.user.id), 0);
        setTimeout(() => checkApproval(session.user.id), 0);
      } else {
        setDedicatedSubscription({ subscribed: false, subscriptionEnd: null, loading: false });
        setPasswordExpired(false);
        setIsApproved(false);
        setApprovalLoading(false);
        try { sessionStorage.removeItem("dedicated_sub_cache_v1"); } catch { /* ignore */ }
      }
    });

    // Note: onAuthStateChange fires INITIAL_SESSION on mount, so no need to also call getSession() here.

    return () => subscription.unsubscribe();
  }, [checkSubscription, checkPasswordExpiry, checkApproval]);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut, passwordExpired, isApproved, approvalLoading, dedicatedSubscription, refreshSubscription: () => checkSubscription(true) }}>
      {children}
    </AuthContext.Provider>
  );
};
